import { useCallback, useEffect, useRef, useState } from "react";
import { useRequest } from "../../hooks/commonHooks";
import { MATCHING_SERIAL_STATUS_DELETED, MATCHING_SERIAL_STATUS_NORMAL, MatchingBatchPlotData, MatchingBatchPlotDataPaired, MatchingBatchPlotResult } from "../../models/testing_model";
import { Button, Dimmer, Divider, Dropdown, DropdownItem, DropdownMenu, Input, Loader } from "semantic-ui-react";
import { untilStable } from "../../utils";
import * as d3 from "d3";
import $ from "jquery";
import _ from "lodash";
import { PlotChannelColors, PlotChannelDeepColors } from "../../models/common_model";
import { notificationControl } from "../../controls/notification-control";
import { dialogControl } from "../../controls/dialog-control";
import { restRequest } from "../../http";
import { useCurrentUser } from "../../hooks/auth-hook";


const selectedColor1 = '#2408d7';
const selectedColor2 = '#da1b1b';

export function MatchingView(props: { match: any }) {
    const id = props.match.params.id - 0;
    const selectedSNRef = useRef<string>(props.match.params.sn || '');
    const sidebarRef = useRef<HTMLDivElement>(null);
    const sidebarItemsRef = useRef<HTMLDivElement>(null);
    const [data, setData] = useState<MatchingBatchPlotData>();
    const [allPaireds, setAllParieds] = useState<MatchingBatchPlotDataPaired[]>([]);
    const [filteredParieds, setFilteredParieds] = useState<MatchingBatchPlotDataPaired[]>([]);
    const [height, setHeight] = useState<number>(100);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const [statuses, setStatuses] = useState<number[]>([]);

    const [searchKey, setSearchKey] = useState('');

    const currentUser = useCurrentUser();

    const [rawData, loading] = useRequest<MatchingBatchPlotResult>('testings/get_matching_batch_plot_data', { batch_id: id }, 'post');

    useEffect(() => {
        if (!rawData) {
            return;
        }

        const xs = JSON.parse(rawData?.xs || '[]') as number[];
        const rawLimit = JSON.parse(rawData?.limit || '[]') as number[][];
        const limitScale = d3.scaleLinear().domain(rawLimit[0]!).range(rawLimit[1]!);
        const limit = xs.map(x => limitScale(x));

        let minY = 999999;
        let maxY = -999999;

        let absMinY = d3.min(limit)!;
        let absMaxY = d3.max(limit)!;


        const paireds = rawData?.paireds.map(d => {
            const ys = JSON.parse(d.ys) as number[];
            const ys2 = JSON.parse(d.ys2) as number[];
            const abs = ys.map((v, i) => Math.abs(v - ys2[i]));

            minY = d3.min([minY, ...ys, ...ys2])!;
            maxY = d3.max([maxY, ...ys, ...ys2])!;

            absMinY = d3.min([absMinY, ...abs])!;
            absMaxY = d3.max([absMaxY, ...abs])!;

            return { ...d, ys, ys2, abs };
        }) || [];

        setAllParieds(paireds);

        const newData: MatchingBatchPlotData = {
            ...rawData, xs, limit,

            xDomain: [0, 0],
            yDomain: [0, 0],
            absYDomain: [0, 0]
        }

        newData.xDomain[0] = d3.min(xs)!;
        newData.xDomain[1] = d3.max(xs)!;
        newData.yDomain[0] = minY;
        newData.yDomain[1] = maxY;
        newData.absYDomain[0] = absMinY;
        newData.absYDomain[1] = absMaxY;

        setData(newData);

        window.document.title = `${rawData?.name} MATCHED STEREO PAIR`;

        setHeight(window.innerHeight - $(sidebarItemsRef.current!).offset()!.top - 2);
    }, [rawData]);


    const onScrollSelected = useCallback((direction: number) => {
        if (filteredParieds.length > 0) {
            let currentIndex = selectedIndex
            if (currentIndex !== -1) {
                currentIndex += direction;

                if (currentIndex === -1) {
                    currentIndex = filteredParieds.length - 1;
                } else if (currentIndex >= filteredParieds.length) {
                    currentIndex = 0
                }

                setSelectedIndex(currentIndex)
            }
        }
    }, [filteredParieds, selectedIndex]);


    useEffect(() => {
        const key = (searchKey || '').toUpperCase();

        let selected: MatchingBatchPlotDataPaired[] = []
        if (!key) {
            selected = allPaireds;
        } else {
            selected = allPaireds.filter(v => v.name[0].toUpperCase().includes(key) || v.name[1].toUpperCase().includes(key));
        }

        setFilteredParieds(selected)
        setStatuses(selected.map(v => v.status));

        if (allPaireds.length > 0 && selectedSNRef.current) {
            // selected the searching serial on startup
            const key = selectedSNRef.current.toUpperCase();
            selectedSNRef.current = '';
            setSelectedIndex(selected.findIndex(v => v.name[0].toUpperCase().includes(key) || v.name[1].toUpperCase().includes(key)));
        } else {
            setSelectedIndex(-1);
        }
    }, [searchKey, allPaireds]);

    useEffect(() => {
        if (selectedIndex >= 0) {
            setTimeout(() => {
                if (sidebarItemsRef.current) {
                    const activeItem = sidebarItemsRef.current.querySelector('.active') as HTMLDivElement;
                    if (activeItem) {
                        const itemY = activeItem.offsetHeight * selectedIndex;
                        if (itemY < sidebarItemsRef.current.scrollTop) {
                            sidebarItemsRef.current.scrollTop = itemY;
                        }

                        if (itemY + activeItem.offsetHeight * 2 > sidebarItemsRef.current.scrollTop + sidebarItemsRef.current.offsetHeight) {
                            sidebarItemsRef.current.scrollTop = itemY;
                        }
                    }
                }
            }, 200);
        }
    }, [selectedIndex])


    useEffect(() => {
        /*
        arrow left：37
        arrow up：38
        arrow right：39
        arrow down：40
        */
        const onKeyDown = (e: JQuery.KeyDownEvent) => {
            if (e.keyCode == 38) {
                // arrow up
                onScrollSelected(-1);
            } else if (e.keyCode == 40) {
                // arrow down
                onScrollSelected(1);
            }
        }

        $(document).on('keydown', onKeyDown);

        return () => {
            $(document).off('keydown', onKeyDown);
        }
    }, [onScrollSelected]);

    return <div style={{ display: 'flex', flexDirection: 'row', height: height ? (height + 'px') : 'auto' }}>
        <div style={{ width: "300px", height: '100%', flex: 'none', display: 'flex', flexDirection: 'column', padding: '0.5rem' }} ref={sidebarRef}>
            <div ref={sidebarItemsRef} className="ui vertical menu" style={{ flex: '1', height: '100%', width: '100%', margin: '0px', overflow: 'auto' }}>
                <div className="ui side-bar-item item" onKeyUp={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                    <Input fluid icon="search" value={searchKey} onChange={(p, v) => setSearchKey(v.value || '')} />
                </div>

                {filteredParieds.map((p, i) => <div onClick={e => setSelectedIndex(i)}
                    className={`ui side-bar-item item ${selectedIndex === i ? 'active' : ''}`}
                    key={i}
                    style={{ textDecorationLine: statuses[i] == 2 ? 'line-through' : 'inherit' }}
                >

                    {selectedIndex === i ?
                        <><span style={{ color: selectedColor1 }}>{p.name[0]}</span>_&_<span style={{ color: selectedColor2 }}>{p.name[1]}</span></>
                        : <>{p.name[0]}_&_{p.name[1]}</>}

                    <Dropdown floating className="right">
                        <DropdownMenu>
                            {currentUser.permissions.testing.matching.delete && statuses[i] == MATCHING_SERIAL_STATUS_NORMAL && <DropdownItem text='Delete' onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();

                                dialogControl.deletionConfirm(`matching paired ${p.name.join('_&_')}`, agree => {
                                    if (agree) {
                                        return restRequest<number>('testings/delete_matching_serial', { batch_id: id, serials: p.name }, 'post').then((resp) => {
                                            setStatuses(ss => ss.map((s, si) => si == i ? resp : s));
                                        });
                                    }

                                    return Promise.resolve();
                                });
                            }}
                            />}
                            {currentUser.permissions.testing.matching.undelete && statuses[i] == MATCHING_SERIAL_STATUS_DELETED && <DropdownItem text='Undelete' onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();

                                dialogControl.deletionConfirm('', agree => {
                                    if (agree) {
                                        return restRequest<number>('testings/undelete_matching_serial', { batch_id: id, serials: p.name }, 'post').then((resp) => {
                                            setStatuses(ss => ss.map((s, si) => si == i ? resp : s));
                                        });
                                    }

                                    return Promise.resolve();
                                }, undefined, `Are you sure to Undelete matching paired ${p.name.join('_&_')}?`);
                            }}
                            />}
                            <DropdownItem text='Copy' onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();

                                const text = p.name.join('_&_');
                                navigator.clipboard.writeText(text).then(() => {
                                    notificationControl.showSuccess('The Serials been copied!')
                                }).catch(err => {
                                    dialogControl.alert(`The Serials is: ${text}`)
                                });
                            }} />
                        </DropdownMenu>
                    </Dropdown>
                </div>)}
            </div>
            {/* <div style={{ position: 'absolute', left: '-12px', top: '8rem', opacity: 0.618 }}>
                <Button icon="arrow up" size="mini" onClick={e => onScrollSelected(-1)}></Button>
                <Button icon="arrow down" size="mini" onClick={e => onScrollSelected(1)} style={{ display: 'block', marginTop: '2px', marginBottom: '2px' }}></Button>
            </div> */}
            <div style={{ position: 'absolute', left: '90px', bottom: '2.5rem', opacity: 0.618, display: loading ? 'inherit' : 'none' }}>
                <Dimmer active inverted>
                    <Loader inverted></Loader>
                </Dimmer>
            </div>
        </div>
        <div style={{ flex: '1' }}>
            {
                data && <FreqPlot data={data} paireds={filteredParieds} selectedIndex={selectedIndex} onSelectIndex={setSelectedIndex} ></FreqPlot>
            }
        </div>
    </div>
}


function FreqPlot(props: { data: MatchingBatchPlotData, paireds: MatchingBatchPlotDataPaired[], selectedIndex: number, onSelectIndex: (index: number) => void }) {
    const { data, paireds, selectedIndex, onSelectIndex } = props;
    const svgRootRef = useRef<HTMLDivElement>(null);
    const sFullSize = useState<number[]>();
    const sSelectedXY = useState<[number, number]>([0, 0]);
    const sMouseXY = useState<[number, number]>([0, 0]);
    const canvastRef = useRef<HTMLCanvasElement>();
    const canvasContextRef = useRef<CanvasRenderingContext2D>();
    const operationContextRef = useRef<CanvasRenderingContext2D>();
    const xRef = useRef<d3.ScaleLogarithmic<number, number, never> | d3.ScaleLinear<number, number, never>>();
    const yRef = useRef<d3.ScaleLinear<number, number, never>>();
    const absYRef = useRef<d3.ScaleLinear<number, number, never>>();
    const minYRef = useRef(1);
    const absMinYRef = useRef(1);
    const lineRef = useRef<d3.Line<number>>();
    const absLineRef = useRef<d3.Line<number>>();
    const bisectDate = d3.bisector<number, number>(function (d) { return d }).left;
    const margin = { top: 50, right: 50, bottom: 30, left: 50 };
    const absMargin = { top: 60, right: 50, bottom: 50, left: 50 };
    const mainPlotRatio = 2.0 / 3;
    const [mouseOverSelectedIndex, setMouseOverSelectedIndex] = useState(-1);
    const [isShowInfo, setInfoStatus] = useState(false);
    const showInfoTimeoutRef = useRef<number>(0);

    const [operationReady, setOperationReady] = useState(false);
    const [axisReady, setAxisReady] = useState(false);

    const rePlotCountRef = useRef(0);

    const triggerShowInfo = useCallback((forceClose = false) => {
        setInfoStatus(false);

        clearTimeout(showInfoTimeoutRef.current);

        if (!forceClose) {
            showInfoTimeoutRef.current = setTimeout(() => {
                setInfoStatus(true);
            }, 600) as unknown as number;
        }
    }, [setInfoStatus]);


    useEffect(() => {
        return untilStable<number>(() => svgRootRef.current!.offsetWidth, (svgWidth) => {
            const fullWidth = svgWidth;
            const totalHeight = window.innerHeight - $(svgRootRef.current!).offset()!.top - 10;
            const fullHeight = Math.min(fullWidth * 0.618, totalHeight);

            let $canvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.base');
            let $operationCanvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.operation');

            if ($canvas.size() === 0) {
                $canvas = d3.select(svgRootRef.current!)
                    .append("canvas")
                    .attr('class', 'base');

                $operationCanvas = d3.select(svgRootRef.current!)
                    .append("canvas")
                    .attr('class', 'operation')
                    .style('background', 'rgba(255,255,255,0)')
                    .style('position', 'absolute')
                    .style('z-index', '1')
                    .style('top', '0')
                    .style('left', '0');
            }

            const canvas = $canvas.node()!;
            const operationCanvas = $operationCanvas.node()!;

            const dpr = 1; //window.devicePixelRatio || 1;

            canvas.width = fullWidth * dpr;
            canvas.height = fullHeight * dpr;
            operationCanvas.width = fullWidth * dpr;
            operationCanvas.height = fullHeight * dpr;

            sFullSize[1]([fullWidth, fullHeight]);
            canvastRef.current = canvas;
            canvasContextRef.current = canvastRef.current.getContext('2d')!;
            operationContextRef.current = operationCanvas.getContext('2d')!;

            canvasContextRef.current!.scale(dpr, dpr);
            operationContextRef.current!.scale(dpr, dpr);
            canvasContextRef.current.imageSmoothingEnabled = true;
            operationContextRef.current.imageSmoothingEnabled = true;

            const absStartY = fullHeight * mainPlotRatio + absMargin.top / 2;

            let currentSelectedIndex = -1;

            $operationCanvas.on('mouseover', function () {
                //todo hide cursor lines
            }).on('mouseout', function () {
                sSelectedXY[1]([0, 0]);
                setMouseOverSelectedIndex(-1);

                triggerShowInfo(true);
            })
                .on('mousemove', function (event) {
                    (window as any).ff = yRef.current

                    if (xRef.current && yRef.current) {
                        const mouse = d3.pointer(event);

                        sMouseXY[1](mouse);

                        if (mouse[1] < absStartY) {
                            // main plot selection

                            const mouseX = xRef.current.invert(mouse[0] - margin.left);
                            const mouseY = yRef.current.invert(mouse[1]);

                            let xIndex = bisectDate(data.xs, mouseX);

                            const selectedUnits: [number, number][] = [];
                            if (xIndex > 0 && xIndex < data.xs.length) {
                                if (xIndex > 0 && mouseX - data.xs[xIndex - 1] < data.xs[xIndex] - mouseX) {
                                    xIndex -= 1;
                                }

                                paireds.forEach((p, index) => {
                                    const yDiff = Math.min(Math.abs(p.ys[xIndex] - mouseY), Math.abs(p.ys2[xIndex] - mouseY));
                                    if (yDiff < minYRef.current) {
                                        selectedUnits.push([yDiff, index])
                                    }
                                });
                            }

                            const selectedVectorData = selectedUnits.sort((a, b) => a[0] > b[0] ? 1 : (a[0] === b[0] ? 0 : -1));
                            if (selectedVectorData.length > 0) {
                                currentSelectedIndex = selectedVectorData[0][1];
                            } else {
                                currentSelectedIndex = -1;
                            }
                            setMouseOverSelectedIndex(currentSelectedIndex);

                            sSelectedXY[1]([mouseX, mouseY]);
                            triggerShowInfo();
                        } else {
                            // absolute plot selection

                            const mouseX = xRef.current.invert(mouse[0] - margin.left);
                            const mouseY = absYRef.current!.invert(mouse[1]);

                            let xIndex = bisectDate(data.xs, mouseX);

                            const selectedUnits: [number, number][] = [];
                            if (xIndex > 0 && xIndex < data.xs.length) {
                                if (xIndex > 0 && mouseX - data.xs[xIndex - 1] < data.xs[xIndex] - mouseX) {
                                    xIndex -= 1;
                                }

                                paireds.forEach((p, index) => {
                                    const yDiff = Math.min(Math.abs(p.abs[xIndex] - mouseY));
                                    if (yDiff < absMinYRef.current) {
                                        selectedUnits.push([yDiff, index])
                                    }
                                });
                            }

                            const selectedVectorData = selectedUnits.sort((a, b) => a[0] > b[0] ? 1 : (a[0] === b[0] ? 0 : -1));
                            if (selectedVectorData.length > 0) {
                                currentSelectedIndex = selectedVectorData[0][1];
                            } else {
                                currentSelectedIndex = -1;
                            }
                            setMouseOverSelectedIndex(currentSelectedIndex);

                            sSelectedXY[1]([mouseX, mouseY]);
                            triggerShowInfo();
                        }
                    }
                }).on('click', function () {
                    onSelectIndex(currentSelectedIndex);
                });

            setOperationReady(true);
        }, 200);
    }, [data, paireds, onSelectIndex]);

    // render coord and curve
    useEffect(() => {
        if (!operationReady) {
            return;
        }

        const [fullWidth, fullHeight] = sFullSize[0]!;
        const context = canvasContextRef.current!;
        context.clearRect(0, 0, fullWidth, fullHeight);


        const width = fullWidth - margin.left - margin.right;
        const height = fullHeight * mainPlotRatio - margin.top - margin.bottom;
        const absTop = fullHeight * mainPlotRatio;
        const absHeight = fullHeight * (1 - mainPlotRatio) - absMargin.top - absMargin.bottom;

        const xDomain = data.xDomain;
        const yDomain = data.yDomain;
        const x = d3.scaleLog().domain(xDomain).range([margin.left, width - margin.right]);
        xRef.current = x;
        const xAxis = (top: number, height: number, bottop: number) => {
            const tickCount = 10,
                tickSize = 6,
                ticks = x.ticks(tickCount),
                tickFormat = x.tickFormat(tickCount, 'd');

            context.strokeStyle = "gray";
            context.beginPath();
            ticks.forEach(function (d) {
                context.moveTo(x(d), top + height);
                context.lineTo(x(d), top);
            });
            context.stroke();

            const xAxisTitle = xDomain[xDomain.length - 1] >= 1500 ? 'Frequency(Hz)' : '';
            context.strokeStyle = "black";
            context.beginPath();
            const metrics = context.measureText(xAxisTitle);
            const textWidth = metrics.width;
            context.fillText(xAxisTitle, x(xDomain[0]) + x(xDomain[1]) / 2 - x(xDomain[0]) / 2 - textWidth / 2, top + height + bottop / 2);

            context.strokeStyle = "black";
            context.beginPath();
            context.lineTo(0, top + height);
            context.lineTo(width, top + height);
            context.stroke();

            context.textAlign = "center";
            context.textBaseline = "top";
            ticks.forEach(function (d) {
                context.fillText(tickFormat(d), x(d), top + height);
            });
        }

        const y = d3.scaleLinear().domain(yDomain).range([height + margin.top, margin.top]);
        yRef.current = y;
        const yRangeStart = y.range()[0];
        minYRef.current = Math.abs(y.invert(yRangeStart) - y.invert(yRangeStart - 2));

        const absY = d3.scaleLinear().domain(data.absYDomain).range([absTop + absHeight + absMargin.top, absTop + absMargin.top]);
        absYRef.current = absY
        const yAbsRangeStart = absY.range()[0];
        absMinYRef.current = Math.abs(absY.invert(yAbsRangeStart) - absY.invert(yAbsRangeStart - 2));

        const yAxis = (top: number, y: d3.ScaleLinear<number, number, never>, title: string) => {
            const domain = y.domain();
            const height = Math.abs(y(domain[1]) - y(domain[0]));

            const xStart = x(xDomain[0])
            const xEnd = x(xDomain[1])

            const tickCount = 10,
                tickSize = 6,
                tickPadding = 3,
                ticks = y.clamp(true).ticks(tickCount),
                tickFormat = y.tickFormat(tickCount);

            context.beginPath();
            ticks.forEach(function (d) {
                context.moveTo(xStart, y(d));
                context.lineTo(xEnd, y(d));
            });
            context.strokeStyle = "black";
            context.stroke();

            context.beginPath();
            context.moveTo(xStart - tickSize, top);
            context.lineTo(xStart + 0.5, top);
            context.lineTo(xStart + 0.5, top + height);
            context.lineTo(xStart - tickSize, top + height);
            context.strokeStyle = "black";
            context.stroke();

            context.textAlign = "right";
            context.textBaseline = "middle";
            ticks.forEach(function (d) {
                context.fillText(tickFormat(d), xStart - tickSize - tickPadding, y(d));
            });

            const yAxisTitle = title;
            context.save();
            context.rotate(-Math.PI / 2);
            context.textAlign = "right";
            context.textBaseline = "top";
            context.strokeStyle = "black";
            const metrics = context.measureText(yAxisTitle);
            const textWidth = metrics.width;
            context.fillText(yAxisTitle, - (height / 2) + textWidth / 2 - top, xStart - margin.left);
            context.restore();
        }

        const lineFun = d3.line<number>()
            .x(function (d: number, i: number) { return x(data.xs[i]) })
            .y(function (d: number, i: number) { return y(d) });

        const absLineFun = d3.line<number>()
            .x(function (d: number, i: number) { return x(data.xs[i]) })
            .y(function (d: number, i: number) { return absY(d) });

        lineRef.current = lineFun;
        absLineRef.current = absLineFun;


        const line = lineFun.context(context);
        const absLine = absLineFun.context(context);

        context.save();
        context.translate(margin.left, 0);
        xAxis(margin.top, height, margin.bottom);
        yAxis(margin.top, y, 'Sensitivity');

        xAxis(absTop + absMargin.top, absHeight, absMargin.bottom);
        yAxis(absTop + absMargin.top, absY, 'Absolute Difference');

        context.lineWidth = 1;
        context.strokeStyle = PlotChannelColors[0];
        context.beginPath();
        for (const p of paireds) {
            line(p.ys);
        }
        context.stroke();
        context.strokeStyle = PlotChannelColors[1] + '80';
        context.beginPath();
        for (const p of paireds) {
            line(p.ys2);
        }
        context.stroke();

        context.lineWidth = 1;
        context.strokeStyle = PlotChannelDeepColors[0];
        context.beginPath();
        for (const p of paireds) {
            absLine(p.abs);
        }
        context.stroke();

        context.beginPath();
        absLine(data.limit);
        context.lineWidth = 3;
        context.strokeStyle = "#FF0000";
        context.stroke();

        context.restore();

        setAxisReady(true);
        return () => {
            rePlotCountRef.current += 1;
            context.clearRect(0, 0, fullWidth, fullHeight);
        }
    }, [data, paireds, operationReady, rePlotCountRef.current]);

    // render selected curve
    useEffect(() => {
        if (!operationReady || !axisReady) {
            return;
        }

        const operationContext = operationContextRef.current;
        if (operationContext && sFullSize[0]) {
            operationContext.clearRect(0, 0, sFullSize[0][0], sFullSize[0][1]);
            if (lineRef.current) {
                const line = lineRef.current.context(operationContext);
                const absLine = absLineRef.current!.context(operationContext);
                operationContext.save();
                operationContext.translate(margin.left, 0);

                if (selectedIndex >= 0) {
                    const p = paireds[selectedIndex];
                    if (p) {
                        operationContext.lineWidth = 1;
                        operationContext.strokeStyle = selectedColor1;
                        operationContext.beginPath();
                        line(p.ys);
                        operationContext.stroke();

                        operationContext.strokeStyle = selectedColor2;
                        operationContext.beginPath();
                        line(p.ys2);
                        operationContext.stroke();

                        operationContext.strokeStyle = '#127812';
                        operationContext.beginPath();
                        absLine(p.abs);
                        operationContext.stroke();
                    }
                }

                if (mouseOverSelectedIndex >= 0 && mouseOverSelectedIndex != selectedIndex) {
                    const p = paireds[mouseOverSelectedIndex];
                    if (p) {
                        operationContext.lineWidth = 1;
                        operationContext.strokeStyle = selectedColor1 + '80';
                        operationContext.beginPath();
                        line(p.ys);
                        operationContext.stroke();

                        operationContext.strokeStyle = selectedColor2 + '80';
                        operationContext.beginPath();
                        line(p.ys2);
                        operationContext.stroke();

                        operationContext.strokeStyle = '#12781280';
                        operationContext.beginPath();
                        absLine(p.abs);
                        operationContext.stroke();
                    }
                }

                operationContext.restore();

                if (selectedIndex >= 0) {
                    canvastRef.current!.style.opacity = '0.3';
                } else {
                    canvastRef.current!.style.opacity = '1';
                }
            }
        }
    }, [paireds, mouseOverSelectedIndex, selectedIndex, operationReady, axisReady])


    return <div style={{ position: 'relative', width: `100%` }}>
        <div ref={svgRootRef} />

        <div style={{ padding: '3px', borderRadius: '3px', position: 'absolute', zIndex: 3, transition: 'opacity ease-out 300ms', opacity: isShowInfo ? 0.618 : 0, background: '#333', color: '#ccc', pointerEvents: 'none', top: sMouseXY[0][1] - 35 + 'px', left: sMouseXY[0][0] + 20 + 'px' }}>{mouseOverSelectedIndex >= 0 ? (paireds[mouseOverSelectedIndex].name.join('_&_') + ' ') : ''}(x: {sSelectedXY[0][0].toFixed(2)}, y: {sSelectedXY[0][1].toFixed(2)})</div>
    </div>
}